import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import FormGraphNodeConfiguration from 'dashboard/forms/FormGraphNodeConfiguration';
import { GraphNodeConfigFormData } from 'dashboard/forms/FormGraphNodeConfiguration/FormGraphNodeConfiguration';
import { Container, Section as LayoutSection } from 'dashboard/layouts/IndexViewLayoutV2';
import { AccountType } from 'dashboard/models/account-type';
import { Graph, GraphNodeConfigAttributes } from 'dashboard/models/graph';
import { GraphDatumType, GraphDatumTypeAttributes } from 'dashboard/models/graph-datum-type';

export interface DashboardGraphNodeConfigViewProps {
  graph?: Graph;
  graphDatumType?: GraphDatumType;
  accountTypes: AccountType[];
  isLoading: boolean;
  isSubmitting: boolean;
  handleUpdateGraphNodeConfig: (config: GraphNodeConfigAttributes) => Promise<void>;
  handleUpdateGraphDatumType: (
    graphDatumTypeId: string,
    config: GraphDatumTypeAttributes,
  ) => Promise<void>;
}

const DashboardGraphNodeConfigView = ({
  graph,
  graphDatumType,
  accountTypes,
  isLoading,
  isSubmitting,
  handleUpdateGraphNodeConfig,
  handleUpdateGraphDatumType,
}: DashboardGraphNodeConfigViewProps) => {
  const nodeFunctionsConfig = graph?.attributes['node-functions-configs']?.find(
    (nodeFunctionsConfig) => nodeFunctionsConfig['datum-type-token'] === graphDatumType?.id,
  );
  const nodeRenderConfig = graph?.attributes['node-render-configs']?.find(
    (nodeRenderConfig) => nodeRenderConfig['datum-type-token'] === graphDatumType?.id,
  );
  const isCustomNode = !!(
    graphDatumType?.id &&
    graph?.attributes['node-types'].map((x) => x['datum-type-token']).includes(graphDatumType?.id)
  );

  const handleSubmitForm = (data: GraphNodeConfigFormData) => {
    if (graph && (data.nodeRenderConfig || data.nodeFunctionsConfig)) {
      handleUpdateGraphNodeConfig({
        'datum-type-token': graphDatumType?.id || '',
        'node-render-config': data.nodeRenderConfig,
        'node-functions-config': {
          ...data.nodeFunctionsConfig,
          sources: data.nodeFunctionsConfig.sources.map((source) => {
            return {
              ...source,
              'function-mappings': source['function-mappings'].map((fm) => ({
                ...fm,
                'param-mappings': fm['param-mappings'].map((pm) => {
                  const strArr = pm.path.split('.');
                  strArr.shift();
                  return { ...pm, path: strArr.join('.') };
                }),
              })),
              'node-key': source['node-key'].map((nk) => {
                const strArr = nk.path.split('.');
                strArr.shift();
                return { ...nk, path: strArr.join('.') };
              }),
            };
          }),
        },
      });
    }
    if (graphDatumType && data.graphDatumType) {
      handleUpdateGraphDatumType(graphDatumType.id, {
        ...graphDatumType.attributes,
        criteria: { version: 1, rules: data.graphDatumType?.attributes?.criteria?.rules },
      });
    }
  };

  return isLoading || !graph || !graphDatumType ? (
    <Container>
      <LayoutSection>
        <LoadingIndicator />
      </LayoutSection>
    </Container>
  ) : (
    <FormGraphNodeConfiguration
      nodeFunctionsConfig={nodeFunctionsConfig}
      nodeRenderConfig={nodeRenderConfig}
      graphDatumType={graphDatumType}
      accountTypes={accountTypes}
      presetSummaries={graph?.attributes['preset-summaries']}
      defaultGraphProperties={
        graph.attributes['default-graph-properties'][graphDatumType.id.replace('_', '-')]
      }
      handleSubmit={handleSubmitForm}
      isSubmitting={isSubmitting}
      isCustomNode={isCustomNode}
    />
  );
};

export default DashboardGraphNodeConfigView;
